import { useMutation, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  Loader2,
  Minus,
  Sparkles,
  Table2,
  Target,
  TrendingDown,
  TrendingUp,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendBadge } from "../../components/TrendBadge.js";
import {
  type DepartmentRiskProfile,
  type ManagerEffectiveness,
  type TeamsResponse,
  aiApi,
  employeesApi,
} from "../../lib/api.js";

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

type View = "cards" | "table";

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function riskRatio(d: DepartmentRiskProfile): number {
  return d.total > 0 ? d.high / d.total : 0;
}

function threatLevel(d: DepartmentRiskProfile): "critical" | "elevated" | "guarded" | "low" {
  if (d.confirmedLeaving > 0) return "critical";
  if (riskRatio(d) >= 0.4) return "elevated";
  if (riskRatio(d) >= 0.2) return "guarded";
  return "low";
}

// Bewusst reduziert (Design-Regeln 27.07.): keine Glow-/Hintergrund-Effekte,
// nur Farbe + Rahmen. Labels kommen aus i18n (sentence case, kein ALL-CAPS).
// `color` = Marken-Farbe (Marker/Balken), `text` = kontrastsichere Text-Variante.
const THREAT_META = {
  critical: {
    labelKey: "cockpit.departments.level.critical",
    color: "var(--risk-high)",
    text: "var(--risk-high-text)",
    border: "color-mix(in srgb, var(--risk-high) 40%, transparent)",
  },
  elevated: {
    labelKey: "cockpit.departments.level.elevated",
    color: "var(--accent)",
    text: "var(--accent)",
    border: "color-mix(in srgb, var(--accent) 35%, transparent)",
  },
  guarded: {
    labelKey: "cockpit.departments.level.guarded",
    color: "var(--risk-medium)",
    text: "var(--risk-medium-text)",
    border: "color-mix(in srgb, var(--risk-medium) 25%, transparent)",
  },
  low: {
    labelKey: "cockpit.departments.level.low",
    color: "var(--risk-low)",
    text: "var(--risk-low-text)",
    border: "var(--border)",
  },
} as const;

// Trend-Pfeil fürs Benchmarking: sinkender Score = besser (grün).
function TrendIcon({ trend }: { trend: ManagerEffectiveness["trend"] }) {
  if (trend === "improving") return <TrendingDown size={11} style={{ color: "var(--risk-low)" }} />;
  if (trend === "declining") return <TrendingUp size={11} style={{ color: "var(--risk-high)" }} />;
  return <Minus size={11} style={{ color: "var(--text-muted)" }} />;
}

// ── Zone 3: Abteilungen ───────────────────────────────────────────────────────

export function Departments(props: { teamsQuery: UseQueryResult<TeamsResponse> }) {
  const { t } = useTranslation();
  const [view, setView] = useState<View>("cards");
  const [expandedDept, setExpandedDept] = useState<string | null>(null);
  const [briefings, setBriefings] = useState<Record<string, string>>({});

  const briefingMutation = useMutation({
    mutationFn: (dept: string) => aiApi.getBriefing(dept),
    onSuccess: (res) => setBriefings((prev) => ({ ...prev, [res.managerId]: res.briefing })),
  });

  // Benchmarking-Daten erst laden, wenn die Tabellen-Ansicht aktiv ist (lazy).
  const { data: effectivenessData } = useQuery({
    queryKey: ["manager-effectiveness"],
    queryFn: employeesApi.getManagerEffectiveness,
    staleTime: 10 * 60_000,
    enabled: view === "table",
    retry: 0,
  });

  const { data, isLoading, isError, error } = props.teamsQuery;

  // Sortierung: bestätigte Abgänge zuerst, dann nach Risikoanteil absteigend.
  const teams = [...(data?.teams ?? [])].sort((a, b) => {
    if (b.confirmedLeaving !== a.confirmedLeaving) return b.confirmedLeaving - a.confirmedLeaving;
    return riskRatio(b) - riskRatio(a);
  });

  return (
    <div className="card p-5 mb-5">
      {/* Kopfzeile: Titel + Umschalter Karten/Tabelle */}
      <div className="flex items-center justify-between mb-4">
        <div
          className="text-[11px] font-bold tracking-widest uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          {t("cockpit.departments.title")}
        </div>
        <div className="segmented">
          {(["cards", "table"] as const).map((k) => (
            <button
              key={k}
              type="button"
              data-active={view === k}
              onClick={() => setView(k)}
              className="flex items-center gap-1"
            >
              {k === "cards" ? <LayoutGrid size={11} /> : <Table2 size={11} />}
              {t(k === "cards" ? "cockpit.departments.cards" : "cockpit.departments.table")}
            </button>
          ))}
        </div>
      </div>

      {/* Lade-/Fehlerzustand der Team-Daten */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              background: "var(--accent-dim)",
              border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
            }}
          >
            <Loader2 size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
          </div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("cockpit.departments.loading")}
          </p>
        </div>
      )}

      {isError && (
        <div
          className="flex items-center gap-3 p-4 mx-auto max-w-md"
          style={{
            background: "var(--risk-high-bg)",
            border: "1px solid color-mix(in srgb, var(--risk-high) 25%, transparent)",
            borderRadius: "var(--radius)",
          }}
        >
          <AlertCircle size={15} style={{ color: "var(--risk-high)", flexShrink: 0 }} />
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {error instanceof Error ? error.message : t("cockpit.departments.loadError")}
          </p>
        </div>
      )}

      {/* ── Karten-Ansicht ─────────────────────────────────────────────── */}
      {!isLoading && !isError && view === "cards" && (
        <div
          className="grid gap-4 stagger"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))" }}
        >
          {teams.map((team) => {
            const threat = threatLevel(team);
            const meta = THREAT_META[threat];
            const isExpanded = expandedDept === team.department;
            const hasBriefing = Boolean(briefings[team.department]);
            const isGenerating =
              briefingMutation.isPending && briefingMutation.variables === team.department;

            return (
              <div
                key={team.department}
                className="card card-hover"
                style={{ borderColor: meta.border, background: "var(--bg-card)" }}
              >
                {/* Kartenkopf — immer sichtbar */}
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none text-left"
                  onClick={() => setExpandedDept(isExpanded ? null : team.department)}
                >
                  <div
                    className="flex items-center justify-center rounded"
                    style={{
                      width: 32,
                      height: 32,
                      background: `color-mix(in srgb, ${meta.color} 8%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${meta.color} 19%, transparent)`,
                      color: meta.color,
                      flexShrink: 0,
                    }}
                  >
                    <Target size={14} />
                  </div>

                  {/* Name + Risikostufe */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-semibold truncate"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {team.department}
                      </span>
                      <span
                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          background: `color-mix(in srgb, ${meta.color} 9%, transparent)`,
                          color: meta.text,
                          border: `1px solid color-mix(in srgb, ${meta.color} 19%, transparent)`,
                        }}
                      >
                        {t(meta.labelKey)}
                      </span>
                      {team.confirmedLeaving > 0 && (
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                          style={{
                            background: "var(--risk-high-bg)",
                            color: "var(--risk-high-text)",
                            border:
                              "1px solid color-mix(in srgb, var(--risk-high) 40%, transparent)",
                          }}
                        >
                          {t("cockpit.departments.leavingConfirmed", {
                            count: team.confirmedLeaving,
                          })}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                      {t("cockpit.departments.employees", { count: team.total })}
                    </span>
                  </div>

                  {/* Risikobalken + Score */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div style={{ width: 100 }}>
                      <div
                        className="flex rounded overflow-hidden h-1.5 mb-1"
                        style={{ background: "var(--bg-hover)" }}
                      >
                        {team.high > 0 && (
                          <div
                            style={{
                              width: `${(team.high / team.total) * 100}%`,
                              background: "var(--risk-high)",
                            }}
                          />
                        )}
                        {team.medium > 0 && (
                          <div
                            style={{
                              width: `${(team.medium / team.total) * 100}%`,
                              background: "var(--risk-medium)",
                            }}
                          />
                        )}
                        {team.low > 0 && (
                          <div
                            style={{
                              width: `${(team.low / team.total) * 100}%`,
                              background: "var(--risk-low)",
                            }}
                          />
                        )}
                      </div>
                      <div
                        className="flex justify-between text-[9px] font-mono-data"
                        style={{ color: "var(--text-muted)" }}
                      >
                        <span style={{ color: "var(--risk-high-text)" }}>
                          {team.high}
                          {t("cockpit.departments.legendHigh")}
                        </span>
                        <span style={{ color: "var(--risk-medium-text)" }}>
                          {team.medium}
                          {t("cockpit.departments.legendMedium")}
                        </span>
                        <span style={{ color: "var(--risk-low-text)" }}>
                          {team.low}
                          {t("cockpit.departments.legendLow")}
                        </span>
                      </div>
                    </div>

                    <div className="text-right" style={{ width: 60 }}>
                      <div className="flex items-center gap-1.5 justify-end">
                        <div
                          className="text-sm font-bold font-mono-data leading-none"
                          style={{ color: meta.text }}
                        >
                          {pct(team.avgScore)}
                        </div>
                        <TrendBadge trendPp={team.trendPp} />
                      </div>
                      <div
                        className="text-[8px] mt-0.5 font-mono-data"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {t("cockpit.departments.avgRiskMini")}
                      </div>
                    </div>

                    <div style={{ color: "var(--text-muted)" }}>
                      {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </div>
                  </div>
                </button>

                {/* Aufgeklappte Details */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid var(--border)" }}>
                    <div className="grid gap-0" style={{ gridTemplateColumns: "1fr 1fr" }}>
                      {/* Links: Hochrisiko-Personen */}
                      <div className="p-4" style={{ borderRight: "1px solid var(--border)" }}>
                        <div
                          className="text-[10px] font-bold tracking-widest uppercase mb-3 flex items-center gap-1.5"
                          style={{ color: "var(--text-muted)" }}
                        >
                          <AlertTriangle size={10} />
                          {t("cockpit.departments.topRisks")}
                        </div>

                        {team.topRiskEmployees.length === 0 ? (
                          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                            {t("cockpit.departments.noRisks")}
                          </p>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {team.topRiskEmployees.slice(0, 3).map((emp) => (
                              <div
                                key={emp.id}
                                className="p-2.5"
                                style={{
                                  background: emp.isConfirmedLeaving
                                    ? "var(--risk-high-bg)"
                                    : "var(--bg-hover)",
                                  border: `1px solid ${emp.isConfirmedLeaving ? "color-mix(in srgb, var(--risk-high) 30%, transparent)" : "var(--border)"}`,
                                  borderRadius: "var(--radius)",
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  {emp.isConfirmedLeaving && (
                                    <UserX
                                      size={10}
                                      style={{ color: "var(--risk-high)", flexShrink: 0 }}
                                    />
                                  )}
                                  <span
                                    className="text-xs font-medium flex-1 truncate"
                                    style={{ color: "var(--text-primary)" }}
                                  >
                                    {emp.displayName}
                                  </span>
                                  <span
                                    className="text-[10px] font-bold font-mono-data"
                                    style={{
                                      color:
                                        emp.riskLevel === "high"
                                          ? "var(--risk-high-text)"
                                          : emp.riskLevel === "medium"
                                            ? "var(--risk-medium-text)"
                                            : "var(--risk-low-text)",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {pct(emp.score)}
                                  </span>
                                </div>
                                {emp.isConfirmedLeaving && (
                                  <div
                                    className="text-[9px] font-semibold mb-1"
                                    style={{ color: "var(--risk-high-text)" }}
                                  >
                                    {t("cockpit.departments.leavingFlag")}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {emp.topSignals.slice(0, 2).map((sig) => (
                                    <span
                                      key={sig}
                                      className="text-[9px] px-1.5 py-0.5 rounded"
                                      style={{
                                        background: "var(--bg-hover)",
                                        border: "1px solid var(--border)",
                                        color: "var(--text-muted)",
                                      }}
                                    >
                                      {sig}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Rechts: KI-Briefing */}
                      <div className="p-4">
                        <div
                          className="text-[10px] font-bold tracking-widest uppercase mb-3 flex items-center gap-1.5"
                          style={{ color: "var(--text-muted)" }}
                        >
                          <Sparkles size={10} />
                          {t("cockpit.departments.briefing")}
                        </div>

                        {!hasBriefing && !isGenerating && (
                          <div className="flex flex-col gap-3">
                            <p
                              className="text-[11px] leading-relaxed"
                              style={{ color: "var(--text-muted)" }}
                            >
                              {t("cockpit.departments.briefingIntro")}
                            </p>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold w-full justify-center transition-opacity hover:opacity-90"
                              style={{
                                background: "var(--accent)",
                                color: "#fff",
                                borderRadius: "var(--radius)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                briefingMutation.mutate(team.department);
                              }}
                            >
                              <Sparkles size={11} />
                              {t("cockpit.departments.briefingGenerate")}
                            </button>
                          </div>
                        )}

                        {isGenerating && (
                          <div className="flex flex-col items-center justify-center gap-2 py-6">
                            <Loader2
                              size={16}
                              className="animate-spin"
                              style={{ color: "var(--accent)" }}
                            />
                            <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                              {t("cockpit.departments.briefingRunning")}
                            </p>
                          </div>
                        )}

                        {hasBriefing && (
                          <div>
                            <div
                              className="p-3 text-[11px] leading-relaxed whitespace-pre-line mb-2"
                              style={{
                                background: "var(--accent-dim)",
                                border:
                                  "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
                                borderRadius: "var(--radius)",
                                color: "var(--text-secondary)",
                                maxHeight: 200,
                                overflowY: "auto",
                              }}
                            >
                              {briefings[team.department]}
                            </div>
                            <button
                              type="button"
                              className="text-[9px] px-2 py-1 row-hover"
                              style={{
                                color: "var(--text-muted)",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                briefingMutation.mutate(team.department);
                              }}
                            >
                              {t("cockpit.departments.briefingAgain")}
                            </button>
                          </div>
                        )}

                        {briefingMutation.isError &&
                          briefingMutation.variables === team.department && (
                            <p
                              className="text-[11px] mt-2"
                              style={{ color: "var(--risk-high-text)" }}
                            >
                              {t("cockpit.departments.briefingError")}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Tabellen-Ansicht (Benchmarking) ────────────────────────────── */}
      {!isLoading && !isError && view === "table" && (
        <div>
          {!effectivenessData && (
            <div className="flex items-center justify-center h-20 gap-2">
              <Loader2 size={14} className="animate-spin" style={{ color: "var(--accent)" }} />
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                {t("cockpit.departments.loading")}
              </span>
            </div>
          )}
          {effectivenessData && (
            <>
              {/* Tabellenkopf */}
              <div
                className="grid px-4 py-2 text-[10px] font-semibold uppercase"
                style={{
                  gridTemplateColumns: "1fr 70px 70px 70px 80px 60px",
                  color: "var(--text-muted)",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span>{t("cockpit.departments.colManager")}</span>
                <span>{t("cockpit.departments.colTeam")}</span>
                <span>{t("cockpit.departments.colHigh")}</span>
                <span>{t("cockpit.departments.colScore")}</span>
                <span>{t("cockpit.departments.colRetention")}</span>
                <span>{t("cockpit.departments.colTrend")}</span>
              </div>
              {[...effectivenessData.effectiveness]
                .sort((a, b) => b.avgScore - a.avgScore)
                .map((m) => {
                  // Marker-Farbe (Balken) vs. kontrastsichere Text-Variante
                  const retentionMark =
                    m.retentionRate >= 0.85
                      ? "var(--risk-low)"
                      : m.retentionRate >= 0.7
                        ? "var(--risk-medium)"
                        : "var(--risk-high)";
                  const retentionText =
                    m.retentionRate >= 0.85
                      ? "var(--risk-low-text)"
                      : m.retentionRate >= 0.7
                        ? "var(--risk-medium-text)"
                        : "var(--risk-high-text)";
                  return (
                    <div
                      key={`${m.managerId}-${m.department}`}
                      className="grid items-center px-4 py-2.5 row-hover"
                      style={{
                        gridTemplateColumns: "1fr 70px 70px 70px 80px 60px",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <div>
                        <div
                          className="text-xs font-medium truncate"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {m.managerLabel}
                        </div>
                        {m.department && (
                          <div className="text-[9px]" style={{ color: "var(--text-muted)" }}>
                            {m.department}
                          </div>
                        )}
                      </div>
                      <span
                        className="text-[11px] font-mono-data"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {m.teamSize}
                      </span>
                      <span
                        className="text-[11px] font-mono-data font-bold"
                        style={{
                          color:
                            m.highRiskCount > 0 ? "var(--risk-high-text)" : "var(--text-muted)",
                        }}
                      >
                        {m.highRiskCount}
                      </span>
                      <span
                        className="text-[11px] font-mono-data font-bold"
                        style={{
                          color:
                            m.avgScore >= 0.6
                              ? "var(--risk-high-text)"
                              : m.avgScore >= 0.4
                                ? "var(--risk-medium-text)"
                                : "var(--risk-low-text)",
                        }}
                      >
                        {pct(m.avgScore)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <div
                          className="flex-1 h-1 rounded-full"
                          style={{ background: "var(--border)" }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${m.retentionRate * 100}%`,
                              background: retentionMark,
                            }}
                          />
                        </div>
                        <span
                          className="text-[10px] font-mono-data w-8 text-right"
                          style={{ color: retentionText }}
                        >
                          {Math.round(m.retentionRate * 100)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendIcon trend={m.trend} />
                        <span
                          className="text-[9px]"
                          style={{
                            color:
                              m.trend === "improving"
                                ? "var(--risk-low)"
                                : m.trend === "declining"
                                  ? "var(--risk-high)"
                                  : "var(--text-muted)",
                          }}
                        >
                          {m.trend === "improving"
                            ? t("cockpit.departments.trendBetter")
                            : m.trend === "declining"
                              ? t("cockpit.departments.trendWorse")
                              : t("cockpit.departments.trendStable")}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </>
          )}
        </div>
      )}

      {/* Einmalige Einblende-Animation der Karten (kein Dauereffekt) */}
      <style>{`
        @keyframes deptReveal {
          from { opacity: 0; transform: translateY(8px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
